/**
 * Toggle for users who are both an instructor and a learner (compliance
 * officer types — they teach AML and also take KVKK trainings). Lives at
 * the top of the sidebar; only renders when user.is_instructor is true.
 *
 * Active mode is derived from the URL — anything under /instructor counts
 * as instructor mode, everything else as learner. The localStorage key
 * is just a hint for "which home should Ana Sayfa link to" so picking a
 * mode survives a page refresh.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GraduationCap, BookOpen } from 'lucide-react';

export const VIEW_MODE_KEY = 'preferred_view_mode';

export function getPreferredViewMode() {
  return typeof window !== 'undefined' && localStorage.getItem(VIEW_MODE_KEY) === 'learner'
    ? 'learner'
    : 'instructor';
}

export function getCurrentViewMode(pathname) {
  return pathname.startsWith('/instructor') ? 'instructor' : 'learner';
}

export default function RoleSwitcher({ collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = getCurrentViewMode(location.pathname);

  const switchTo = (mode) => {
    if (mode === current) return;
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* private mode */ }
    navigate(mode === 'instructor' ? '/instructor' : '/dashboard');
  };

  if (collapsed) {
    const next = current === 'instructor' ? 'learner' : 'instructor';
    const Icon = current === 'instructor' ? GraduationCap : BookOpen;
    return (
      <button
        onClick={() => switchTo(next)}
        className="w-full flex items-center justify-center py-2 rounded-lg text-primary hover:bg-muted/50 transition-colors"
        title={next === 'instructor' ? 'Öğretmen moduna geç' : 'Öğrenci moduna geç'}
        data-testid="role-switcher-compact"
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  }

  const pillBase = 'flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors';
  const pillActive = 'bg-background text-foreground shadow-sm';
  const pillInactive = 'text-muted-foreground hover:text-foreground';

  return (
    <div
      className="grid grid-cols-2 gap-1 p-1 mx-3 mt-3 rounded-lg bg-muted/50"
      data-testid="role-switcher"
    >
      <button
        onClick={() => switchTo('learner')}
        className={`${pillBase} ${current === 'learner' ? pillActive : pillInactive}`}
        data-testid="role-switcher-learner"
      >
        <BookOpen className="w-3.5 h-3.5" />
        <span>Öğrenci</span>
      </button>
      <button
        onClick={() => switchTo('instructor')}
        className={`${pillBase} ${current === 'instructor' ? pillActive : pillInactive}`}
        data-testid="role-switcher-instructor"
      >
        <GraduationCap className="w-3.5 h-3.5" />
        <span>Öğretmen</span>
      </button>
    </div>
  );
}
